import React, { useState } from "react";
import { Modal } from "../../../components/modal/Modal";
import { AtiraTabs } from "../../../components/AtiraTabs";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { faGoogle } from "@fortawesome/free-brands-svg-icons/faGoogle";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { Controller, useForm } from "react-hook-form";
import { Regexes } from "../../../utils/String";
import { LoginDto } from "../../../model/user/dto/LoginDto";
import styled from "styled-components";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import isEmpty from "lodash/isEmpty";
import { TestIDs } from "../../../tests/types/testIds";
import { GoogleButtonWindow } from "../../../components/logins/GoogleButtonWindow";
import { User } from "../../../model/user/User";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { AtiraToast } from "../../../utils/AtiraToast";

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const StyledGoogleButton = styled(GoogleButtonWindow)`
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  height: 2.3rem;
  width: 100%;
  border: 1px solid ${(props) => props.theme.transparent};
  transition: all 0.2s;

  &:hover {
    border: 1px solid #c6c7c8;
    background-color: ${(props) => props.theme.darkSub};
  }
`;

const StyledInput = styled(Input)`
  height: 2.3rem;
  border: 1px solid ${(props) => props.theme.lightergray};
  padding: 0.5rem;
  transition: border 0.2s;

  &:focus {
    border: 1px solid #86b7fe !important;
  }
`;

const StyledHr = styled.hr`
  background-color: ${(props) => props.theme.darkerSub};
  height: 1px;
  border: 0;
  opacity: 1;
  width: 100%;
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const SettingsManageAccountsAddAccountModal: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, getValues } = useForm<LoginDto>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onGoogleSuccess = async (newAccount: User) => {
    try {
      await dispatch(
        userActions.manageAccountsAdd({
          userId,
          newAccountId: newAccount?._id,
        }),
      ).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      AtiraToast.success(t("settings.manageAccounts.addModal.onSuccess"));
      onClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const onNext = async () => {
    try {
      setLoading(true);
      await dispatch(
        userActions.manageAccountsAdd({
          userId,
          email: getValues("email").trim(),
          password: getValues("password"),
        }),
      ).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      AtiraToast.success(t("settings.manageAccounts.addModal.onSuccess"));
      onClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Account">
      <AtiraTabs
        items={[
          {
            key: "email",
            label: t("settings.manageAccounts.addModal.tabs.email"),
            icon: <AtiraIcon icon={faEnvelope} />,
            children: (
              <Flex flexDirection="column" gap="m">
                <Text fontWeight={"bold"}>
                  {t("settings.manageAccounts.addModal.tabs.email.title")}
                </Text>
                <Flex
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap="s"
                >
                  <Controller
                    name="email"
                    rules={{ required: true, pattern: Regexes.EMAIL_REGEX }}
                    control={control}
                    render={({
                      field: { value, onChange },
                      formState: { errors },
                    }) => (
                      <StyledInput
                        title={t("common.email")}
                        type="email"
                        value={value}
                        onChange={onChange}
                        placeholder={t("common.email")}
                        id="email"
                        required
                        valid={isEmpty(errors.email)}
                        errorMessage={
                          (errors.email?.message as string) ||
                          t("signup.error.email")
                        }
                        dataTestId={
                          TestIDs.general.settings.manageAccounts.modal.email
                            .email
                        }
                      />
                    )}
                  />

                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: true }}
                    render={({
                      field: { value, onChange },
                      formState: { errors },
                    }) => (
                      <StyledInput
                        title={t("common.password")}
                        value={value}
                        type="password"
                        onChange={onChange}
                        required
                        placeholder={t("common.password")}
                        id="password"
                        valid={isEmpty(errors.password)}
                        errorMessage={
                          (errors.password?.message as string) ||
                          t("signup.error.password")
                        }
                        dataTestId={
                          TestIDs.general.settings.manageAccounts.modal.email
                            .password
                        }
                      />
                    )}
                  />
                </Flex>

                <StyledHr />
                <Flex width="100%">
                  <StyledButton
                    loading={loading}
                    onClick={handleSubmit(onNext)}
                    title={t("common.continue")}
                    dataTestId={
                      TestIDs.general.settings.manageAccounts.modal.email.button
                    }
                  />
                </Flex>
              </Flex>
            ),
          },
          {
            key: "google",
            label: t("settings.manageAccounts.addModal.tabs.google"),
            icon: <AtiraIcon icon={faGoogle} />,
            children: (
              <Flex flexDirection="column" gap="m">
                <Text fontWeight={"bold"}>
                  {t("settings.manageAccounts.addModal.tabs.google.title")}
                </Text>
                <Flex flexDirection="column" gap="m" width="100%" marginTop="m">
                  <StyledGoogleButton onSuccess={onGoogleSuccess}>
                    {t("sign_in_with_google")}
                  </StyledGoogleButton>

                  <StyledHr />
                </Flex>
              </Flex>
            ),
          },
        ]}
      />
    </Modal>
  );
};
